import area from '@turf/area'
import { featureCollection } from '@turf/helpers'
import intersect from '@turf/intersect'
import union from '@turf/union'
import type { Feature, MultiPolygon, Polygon } from 'geojson'
import type { LngLatCoord } from '@gpx-spa/geo-domain'
import { bufferLineStringMeters } from './buffer-line-string-meters.js'

/** Learned path is treated as a Google duplicate when buffered Jaccard is at least this value. */
export const LEARNED_GOOGLE_JACCARD_DUPLICATE_THRESHOLD = 0.9

export type JaccardBufferedPathPairInput = {
  learnedPathId: string
  googlePathId: string
  learned: LngLatCoord[]
  google: LngLatCoord[]
  radiusMeters: number
}

export type JaccardBufferedPathPairResult = {
  learnedPathId: string
  googlePathId: string
  jaccard: number | null
}

function polygonAreaM2(feature: Feature<Polygon | MultiPolygon> | null): number | null {
  if (!feature?.geometry) return null
  try {
    const value = area(feature)
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

function jaccardOfBufferedLines(
  learned: LngLatCoord[],
  google: LngLatCoord[],
  radiusMeters: number
): number | null {
  if (learned.length < 2 || google.length < 2 || radiusMeters <= 0) return null
  const learnedBuffer = bufferLineStringMeters(learned, radiusMeters)
  const googleBuffer = bufferLineStringMeters(google, radiusMeters)
  if (!learnedBuffer || !googleBuffer) return null
  try {
    const pair = featureCollection([learnedBuffer, googleBuffer])
    const overlap = intersect(pair)
    const combined = union(pair)
    const overlapArea = polygonAreaM2(overlap)
    const unionArea = polygonAreaM2(combined)
    if (overlapArea == null || unionArea == null || unionArea <= 0) return null
    return overlapArea / unionArea
  } catch {
    return null
  }
}

export function jaccardBufferedPathPairsSync(
  pairs: JaccardBufferedPathPairInput[]
): JaccardBufferedPathPairResult[] {
  return pairs.map((pair) => ({
    learnedPathId: pair.learnedPathId,
    googlePathId: pair.googlePathId,
    jaccard: jaccardOfBufferedLines(pair.learned, pair.google, pair.radiusMeters),
  }))
}
